/**
 * Default view offsets per viewport type (in meters).
 * Aligned with Revit defaults.
 */
export const DEFAULT_VIEW_OFFSETS = {
    // 2.286m = 7.5 feet

    FloorPlan: { top: 2.286, bottom: 0 },
    CeilingPlan: { top: 0, bottom: 2.286 },
    EngineeringPlan: { top: 2.286, bottom: 0 },
    Section: { top: 50, bottom: 50 },
    Elevation: { top: 50, bottom: 50 },
    Detail: { top: 1, bottom: 1 },
    AreaPlan: { top: 2.286, bottom: 0 },
};

// Heuristic: checks if the given matrix represents a top-view orientation.
export function isTopViewMatrix(matrix) {
    const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);
    const z = new THREE.Vector3(0, 0, 1);
    const direction = z.clone().applyMatrix3(normalMatrix);
    let angle = direction.angleTo(z);
    if (angle >= Math.PI) {
        angle -= Math.PI;
    }

    const AngleThreshold = 0.05;
    return Math.abs(angle) < AngleThreshold;
}

export default class ViewportInterface {
    /**
     * @param {Object} viewportRaw - Raw viewport data from implementing class (e.g. AecModelData or AlignmentService).
     * @param {string} [viewportName=''] - Display name of the viewport.
     * @param {string} sheetUrn - Version URN of the parent sheet.
     * @param {string} sheetName - Display name of the parent sheet.
     * @param {Object} [options] - Subclass-specific options (e.g. sheetNode, aecModelData, fetch callbacks).
     */
    constructor(viewportRaw, viewportName = '', sheetUrn, sheetName, options) {
        this.viewportRaw = viewportRaw;
        this.viewportName = viewportName;
        this.sheetUrn = sheetUrn;
        this.lineageUrn = Autodesk.Viewing.BubbleNode.parseLineageUrnFromEncodedUrn(sheetUrn);
        this.sheetName = sheetName;
        this.options = options;
    }

    /**
     * Returns the unique viewport guid.
     * @returns {string}
     */
    getGuid() {
        throw "getGuid not implemented";
    }

    /**
     * Whether the 2D-to-3D matrix is in model-local coordinates (true) or world coordinates (false).
     * @returns {boolean}
     */
    isMatrixInLocalCoords() {
        throw "isMatrixInLocalCoords not implemented";
    }

    /**
     * Returns the version URN of the sheet containing this viewport.
     * @returns {string}
     */
    getSheetUrn() {
        return this.sheetUrn;
    }

    /**
     * Returns the lineage URN of the sheet containing this viewport.
     * @returns {string}
     */
    getSheetLineageUrn() {
        return this.lineageUrn;
    }

    /**
     * Returns the display name of the sheet containing this viewport.
     * @returns {string}
     */
    getSheetName() {
        return this.sheetName;
    }

    /**
     * Returns the display name of the viewport.
     * @returns {string}
     */
    getName() {
        return this.viewportName;
    }

    /**
     * Returns the 2D bounding box of the viewport on the sheet.
     * @param {number} [unitScale=1.0] - Scale factor to convert from storage units (feet) to model units.
     * @returns {Autodesk.Viewing.Math.Box2 | null}
     */
    getViewportBounds(unitScale = 1.0) {
        throw "getViewportBounds not implemented";
    }

    /**
     * Returns the 4x4 matrix that transforms 2D sheet coordinates into 3D model/world coordinates.
     * @param {number} [unitScale=1.0] - Scale factor to convert from storage units (feet) to model units.
     * @returns {Autodesk.Viewing.Math.Matrix4 | null}
     */
    get2DTo3DMatrix(unitScale = 1.0) {
        throw "get2DTo3DMatrix not implemented";
    }

    /**
     * Returns the inverse of the 2D-to-3D matrix (3D to 2D transform).
     * @param {number} [unitScale=1.0] - Scale factor passed through to {@link get2DTo3DMatrix}.
     * @returns {Autodesk.Viewing.Math.Matrix4 | null}
     */
    get3DTo2DMatrix(unitScale = 1.0) {
        const matrix = this.get2DTo3DMatrix(unitScale).clone();
        return matrix.invert();
    }

    /**
     * Returns the 2D sheet BubbleNode that contains this viewport.
     * @returns {Promise<Autodesk.Viewing.BubbleNode>}
     */
    async getParentSheetNode() {
        throw "getParentSheetNode not implemented";
    }

    /**
     * Returns the 3D BubbleNodes related to this viewport.
     * @returns {Promise<Autodesk.Viewing.BubbleNode[]>}
     */
    async get3DNodesForViewport() {
        throw "get3DNodesForViewport not implemented";
    }

    /**
     * Returns info about the 3D model related to this viewport.
     * @returns {Promise<{versionUrn3D: string, lineageUrn3D: string, name3D: string}>}
     */
    async getRelatedModel3DInfo() {
        throw "getRelatedModel3DInfo not implemented";
    }

    /**
     * Finds the building level that contains this viewport.
     * @returns {{elevation: number, guid?: string, zMin?: number, zMax?: number} | null}
     */
    findLevelForViewport() {
        throw "findLevelForViewport not implemented";
    }

    /**
     * Checks whether this viewport falls within the given building level.
     * @param {{guid?: string, zMin?: number, zMax?: number}} level - Level to test against.
     * @returns {boolean}
     */
    isViewportInLevel(level) {
        throw "isViewportInLevel not implemented";
    }

    /**
     * Whether this viewport supports 2D-to-3D coordinate transformation.
     * @returns {boolean}
     */
    supports2DTo3DTransform() {
        throw "supports2DTo3DTransform not implemented";
    }

    /**
     * Whether this viewport faces up (top-down view).
     * @returns {boolean}
     */
    isTopViewViewport() {
        throw "isTopViewViewport not implemented";
    }

    /**
     * Whether this viewport represents a floor plan.
     * @returns {boolean}
     */
    isFloorPlan() {
        throw "isFloorPlan not implemented";
    }

    /**
     * Returns the viewport's Z elevation extracted from the 2D-to-3D matrix.
     * @returns {number}
     */
    getElevation() {
        return this.get2DTo3DMatrix().elements[14];
    }

    /**
     * Returns the viewport type string (e.g. 'FloorPlan', 'Section', 'Elevation', 'Detail', 'CeilingPlan').
     * @returns {string | null}
     */
    getViewType() {
        return null;
    }

    /**
     * Returns view clipping offsets from the viewport plane, in meters.
     * @returns {{top: number, bottom: number} | null}
     */
    getViewOffsets() {
        return null;
    }
}
